import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../api/client";
import { useAuth } from "../context/AuthContext";
import "./User.css";

const STATUS_STYLES = {
  success: { bg: "rgba(52,211,153,.1)", color: "#34d399", border: "rgba(52,211,153,.2)", label: "Delivered" },
  pending: { bg: "rgba(251,191,36,.1)", color: "#fbbf24", border: "rgba(251,191,36,.2)", label: "Processing" },
  review: { bg: "rgba(109,92,246,.1)", color: "#a78bfa", border: "rgba(109,92,246,.2)", label: "Under Review" },
  partial: { bg: "rgba(251,191,36,.1)", color: "#fbbf24", border: "rgba(251,191,36,.2)", label: "Partially Delivered" },
  refunded: { bg: "rgba(96,165,250,.1)", color: "#60a5fa", border: "rgba(96,165,250,.2)", label: "Refunded" },
  failed: { bg: "rgba(244,63,94,.1)", color: "#f43f5e", border: "rgba(244,63,94,.2)", label: "Failed" },
};

const LEG_COPY = {
  DELIVERED: "Delivered",
  SENT: "Sent to provider",
  PENDING: "Waiting",
  FAILED: "Failed",
  UNKNOWN: "Awaiting confirmation",
};

function StatusBadge({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.pending;
  return (
    <span style={{
      display: "inline-block", padding: "4px 14px", borderRadius: 99, fontSize: 12, fontWeight: 700,
      background: s.bg, color: s.color, border: `1px solid ${s.border}`,
    }}>
      {s.label}
    </span>
  );
}

export default function ViewOrder() {
  const { orderId } = useParams();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/order/${orderId}`).then((r) => setOrder(r.data.order)).catch((e) => setError(e.message));
  }, [orderId]);

  if (error) return <div className="center muted" style={{ padding: "15vh 16px" }}>{error}</div>;
  if (!order) return <div className="center muted" style={{ padding: "15vh 16px" }}>Loading…</div>;

  const multiLeg = order.fulfillment && order.fulfillment.length > 1;

  return (
    <div className="container" style={{ maxWidth: 520, padding: "32px 16px 100px" }}>
      <Link to="/orders" className="muted small" style={{ display: "block", marginBottom: 20 }}>← All orders</Link>

      {/* Receipt Card */}
      <div style={{
        background: "var(--ink-700, #161a24)", border: "1px solid var(--edge, #2a3140)",
        borderRadius: 16, overflow: "hidden",
      }}>
        {/* Header */}
        <div style={{
          background: "linear-gradient(135deg, #6d5cf6, #4f3cc9)",
          padding: "24px 20px", textAlign: "center",
        }}>
          <div style={{ fontSize: 13, color: "rgba(255,255,255,.7)", marginBottom: 4 }}>Order Receipt</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: "#fff" }}>{order.p_info}</div>
          <div style={{ fontSize: 12, color: "rgba(255,255,255,.5)", marginTop: 6 }}>#{order.orderId}</div>
        </div>

        {/* Status */}
        <div style={{ textAlign: "center", padding: "16px 20px 0" }}>
          <StatusBadge status={order.status} />
        </div>

        {/* Details */}
        <div style={{ padding: "20px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 16px" }}>
            <DetailRow label="Pack" value={order.amount || order.p_info} />
            <DetailRow label="Price" value={`₹${order.price}`} bold />
            <DetailRow label="User ID" value={order.userId || "—"} mono />
            {order.zoneId && <DetailRow label="Zone ID" value={order.zoneId} mono />}
            <DetailRow label="Date" value={new Date(order.createdAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })} />
            <DetailRow label="Time" value={new Date(order.createdAt).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })} />
          </div>

          {/* Fulfillment legs */}
          {order.fulfillment && order.fulfillment.length > 0 && (
            <div style={{ marginTop: 20, borderTop: "1px solid var(--edge, #2a3140)", paddingTop: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "var(--mute, #8a93a6)", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 10 }}>
                Delivery Status
              </div>
              {order.fulfillment.map((leg, i) => (
                <div key={i} style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  padding: "8px 0", borderBottom: i < order.fulfillment.length - 1 ? "1px solid rgba(255,255,255,.04)" : "none",
                }}>
                  <span style={{ fontSize: 13 }}>
                    {multiLeg ? `Part ${i + 1}` : "Order"}
                  </span>
                  <span style={{
                    fontSize: 12, fontWeight: 700,
                    color: leg.status === "DELIVERED" ? "#34d399" : leg.status === "FAILED" ? "#f43f5e" : "#fbbf24",
                  }}>
                    {LEG_COPY[leg.status] || leg.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer messages */}
        <div style={{ padding: "0 20px 20px" }}>
          {order.status === "success" && (
            <div style={{ background: "rgba(52,211,153,.08)", border: "1px solid rgba(52,211,153,.15)", borderRadius: 10, padding: 12, fontSize: 13, color: "#34d399", textAlign: "center" }}>
              ✅ This order has been delivered successfully.
            </div>
          )}
          {order.status === "review" && (
            <div style={{ background: "rgba(109,92,246,.08)", border: "1px solid rgba(109,92,246,.15)", borderRadius: 10, padding: 12, fontSize: 13, color: "#a78bfa", textAlign: "center" }}>
              🔍 We're confirming this order with the provider. It'll update within a few hours.
            </div>
          )}
          {order.status === "partial" && (
            <div style={{ background: "rgba(251,191,36,.08)", border: "1px solid rgba(251,191,36,.15)", borderRadius: 10, padding: 12, fontSize: 13, color: "#fbbf24", textAlign: "center" }}>
              ⚠️ Part of this order was delivered. Our team is resolving the rest. <Link to="/support" style={{ color: "#fbbf24" }}>Contact support</Link>
            </div>
          )}
          {order.status === "refunded" && (
            <div style={{ background: "rgba(96,165,250,.08)", border: "1px solid rgba(96,165,250,.15)", borderRadius: 10, padding: 12, fontSize: 13, color: "#60a5fa", textAlign: "center" }}>
              💰 ₹{order.price} has been refunded to your wallet.
            </div>
          )}
          {order.status === "failed" && (
            <div style={{ background: "rgba(244,63,94,.08)", border: "1px solid rgba(244,63,94,.15)", borderRadius: 10, padding: 12, fontSize: 13, color: "#f43f5e", textAlign: "center" }}>
              ❌ This order failed. Your wallet has been refunded.
            </div>
          )}
        </div>
      </div>

      <div style={{ textAlign: "center", marginTop: 16 }}>
        <Link to="/support" className="muted small">Need help with this order? Contact support</Link>
      </div>
    </div>
  );
}

function DetailRow({ label, value, bold, mono }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: "var(--mute, #8a93a6)", marginBottom: 2 }}>{label}</div>
      <div style={{
        fontSize: 14, fontWeight: bold ? 800 : 600,
        fontFamily: mono ? "monospace" : "inherit",
        color: bold ? "#34d399" : "var(--text, #fff)",
      }}>{value}</div>
    </div>
  );
}
